import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FaLeaf, FaRecycle, FaHistory } from 'react-icons/fa';
import environmentalApi from '../../../services/environmentalApi';
import { ChartCard, baseBarOptions } from '../analytics/analyticsShared';

function ScoreRing({ score }) {
  if (score == null) return <div className="text-sm text-gray-400">No score available</div>;
  const color = score >= 70 ? '#1e5c33' : score >= 40 ? '#D98E04' : '#C0392B';
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEEEEE" strokeWidth="3" />
          <circle
            cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${(score / 100) * 97.4} 97.4`} strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ color }}>{score}</div>
      </div>
      <div className="text-sm text-gray-500">Environmental Score<br /><span className="text-xs">out of 100</span></div>
    </div>
  );
}

// A real AI environmental report, generated from the farmer's own
// WasteRequest history via OpenAI. If there's no waste activity logged,
// this says so honestly rather than showing an inflated score.
export default function AiEnvironmentalAnalysis() {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const loadHistory = async () => {
    try {
      const res = await environmentalApi.getMine();
      setHistory(res.reports || []);
    } catch (err) {
      /* interceptor toasts */
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const wasteTypeChartData = useMemo(() => {
    const byType = report?.stats?.byType || {};
    const entries = Object.entries(byType).filter(([, v]) => v > 0);
    return {
      labels: entries.map(([k]) => k.replace(/_/g, ' ')),
      datasets: [{ label: 'kg processed', data: entries.map(([, v]) => v), backgroundColor: '#1e5c33', borderRadius: 4 }],
    };
  }, [report]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await environmentalApi.analyze(90);
      setReport(res.report);
      loadHistory();
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.error || 'Could not generate report.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaLeaf className="text-primary" /> AI Environmental &amp; Waste Analysis
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            Built from your own waste-management activity over the last 90 days.
          </p>
        </div>
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <FaHistory /> {showHistory ? 'Hide' : 'View'} history ({history.length})
        </button>
      </div>

      {showHistory && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3 max-h-64 overflow-y-auto">
          {history.length === 0 ? (
            <p className="text-sm text-gray-400">No past reports yet.</p>
          ) : (
            history.map((r) => (
              <div key={r._id} className="text-sm border-b border-gray-50 pb-2 flex justify-between">
                <span className="text-gray-600">{new Date(r.createdAt).toLocaleString()}</span>
                <span className="font-semibold text-primary">{r.environmentalScore != null ? `${r.environmentalScore}/100` : 'No score'}</span>
              </div>
            ))
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm disabled:opacity-60"
        >
          {generating ? 'Analysing your waste data…' : 'Generate environmental report'}
        </button>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>

      {report && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <ScoreRing score={report.environmentalScore} />
          <p className="text-gray-700">{report.summary}</p>

          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
            Based on {report.stats?.totalRequests ?? 0} waste request(s) logged in the last {report.windowDays} days
            ({report.stats?.completedRequests ?? 0} completed, {report.stats?.totalQuantityKg ?? 0} kg total).
          </div>

          {wasteTypeChartData.labels.length > 0 && (
            <ChartCard title="Waste processed by type (kg)" className="h-56">
              <div className="h-40">
                <Bar data={wasteTypeChartData} options={baseBarOptions} />
              </div>
            </ChartCard>
          )}

          {report.wasteUtilizationObservations?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-1">Waste utilization observations</h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                {report.wasteUtilizationObservations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}

          {report.recyclingOpportunities?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><FaRecycle /> Recycling opportunities</h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                {report.recyclingOpportunities.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}

          {report.carbonReductionSuggestions?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-1">Carbon reduction suggestions</h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                {report.carbonReductionSuggestions.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}

          {report.collectionScheduleRecommendation && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
              <strong>Collection schedule:</strong> {report.collectionScheduleRecommendation}
            </div>
          )}

          <p className="text-xs text-gray-400 italic">{report.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
